/* Real Liberty Copper — heritage campaign sections (heritage-*.liquid).
 *
 *   - reveal-on-scroll for [data-hx-reveal] and image bands
 *   - Standard Coin purchase: coin-reserve-random → /cart/add → cart drawer
 *     (same contract as the live PDP: bare UUID v4 token, `qty` field, and
 *     the hold is released on any failure). The serial flow is NOT here —
 *     coin-lookup.js drives every [data-rlc-specific-form].
 *   - coin photo thumbnails, gallery lightbox, mobile shop bar, smooth
 *     scrolling to #heritage-* anchors
 *
 * Loaded once per section with `defer`; the guard keeps it single-instance.
 * Theme-editor section reloads re-run init on the new DOM.
 */
(function () {
  'use strict';
  if (window.__hxInit) return;
  window.__hxInit = true;

  var root = document.documentElement;
  root.classList.add('hx-js');
  var designMode = !!(window.Shopify && window.Shopify.designMode);
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Reveal on scroll ─────────────────────────────── */
  var revealIO = null;
  if ('IntersectionObserver' in window && !designMode && !reduceMotion) {
    revealIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); revealIO.unobserve(e.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
  }
  function initReveal(scope) {
    scope.querySelectorAll('[data-hx-reveal]:not(.is-in), [data-hx-band]:not(.is-in)').forEach(function (el) {
      if (revealIO) revealIO.observe(el); else el.classList.add('is-in');
    });
  }

  /* ── Helpers ─────────────────────────────────────── */
  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0; return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }
  function postJSON(url, body, keepalive) {
    return fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(body),
      keepalive: !!keepalive
    });
  }
  function headerOffset() {
    var h = document.querySelector('header.sticky, .shopify-section-group-header-group header, header[class*="sticky"]');
    return h ? Math.ceil(h.getBoundingClientRect().height) + 16 : 16;
  }

  /* ── Purchase module ─────────────────────────────── */
  function initBuy(box) {
    if (box.__hxBound) return;
    box.__hxBound = true;

    // Tabs: Standard Coin / Choose Serial Number
    var tabs = [].slice.call(box.querySelectorAll('[data-hx-flow]'));
    var panels = [].slice.call(box.querySelectorAll('[data-hx-panel]'));
    function activate(flow, focus) {
      tabs.forEach(function (t) {
        var on = t.getAttribute('data-hx-flow') === flow;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('tabindex', on ? '0' : '-1');
        if (on && focus) t.focus();
      });
      panels.forEach(function (p) { p.hidden = p.getAttribute('data-hx-panel') !== flow; });
    }
    tabs.forEach(function (t, i) {
      t.addEventListener('click', function () { activate(t.getAttribute('data-hx-flow')); });
      t.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        var n = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
        activate(n.getAttribute('data-hx-flow'), true);
      });
    });

    var form = box.querySelector('[data-hx-std-form]');
    if (!form) return;
    var proxy = box.getAttribute('data-hx-proxy') || '/apps/coin-lookup';
    var campaign = box.getAttribute('data-hx-campaign') || '';
    var qtyInput = form.querySelector('[data-hx-qty-input]');
    var status = form.querySelector('[data-hx-status]');
    var submit = form.querySelector('[data-hx-submit]');
    var maxQty = parseInt(qtyInput && qtyInput.getAttribute('max'), 10) || 20;

    function clampQty(v) { return Math.min(maxQty, Math.max(1, parseInt(v, 10) || 1)); }
    form.querySelectorAll('[data-hx-qty]').forEach(function (b) {
      b.addEventListener('click', function () {
        qtyInput.value = clampQty((parseInt(qtyInput.value, 10) || 1) + parseInt(b.getAttribute('data-hx-qty'), 10));
      });
    });
    if (qtyInput) qtyInput.addEventListener('change', function () { qtyInput.value = clampQty(qtyInput.value); });

    function say(kind, msg) {
      if (!status) return;
      status.textContent = msg || '';
      status.setAttribute('data-rlc-kind', kind || '');
    }

    var busy = false;
    form.addEventListener('submit', async function (e) {
      e.preventDefault();
      if (busy) return;
      busy = true;
      var label = submit ? submit.innerHTML : '';
      if (submit) { submit.disabled = true; submit.textContent = 'Reserving…'; }
      say('loading', '');
      function restore() { if (submit) { submit.disabled = false; submit.innerHTML = label; } busy = false; }

      var idInput = form.querySelector('[name="id"]');
      var variantId = idInput && idInput.value;
      var qty = clampQty(qtyInput ? qtyInput.value : 1);
      var token = uuid();
      // Release whatever the token holds if the add does not complete (a
      // short-stock reply still holds the coins it found). keepalive lets it
      // finish even if we navigate away.
      function release() { postJSON(proxy + '/coin-reserve-random', { token: token, qty: 0 }, true).catch(function () {}); }
      var added = false;
      try {
        var reserve = await postJSON(proxy + '/coin-reserve-random', { token: token, qty: qty }).then(function (r) { return r.json(); });
        var reservedQty = (reserve && reserve.reserved_count) || 0;
        if (!reserve || !reserve.success || reservedQty < 1) {
          release();
          say('error', (reserve && reserve.message) || 'No coins are available right now. Please try again.');
          restore(); return;
        }
        var props = { _reservation_token: token, _reserved_at: new Date().toISOString() };
        if (campaign) props._campaign = campaign;
        var addRes = await postJSON('/cart/add.js', {
          items: [{ id: Number(variantId), quantity: reservedQty, properties: props }],
          sections: 'cart-drawer', sections_url: location.pathname
        });
        var add = await addRes.json();
        if (!addRes.ok) {
          release();
          say('error', add.description || add.message || 'We couldn’t add that to your cart. Please try again.');
          restore(); return;
        }
        added = true;
        if (window.rlcStampLanding) window.rlcStampLanding();
        var store = window.Alpine && window.Alpine.store && window.Alpine.store('cartDrawer');
        var html = add && add.sections && add.sections['cart-drawer'];
        if (store) { if (html) store.openWith(html); else store.openFresh(); }
        else { window.location.href = '/cart'; return; }
        if (reservedQty < qty) {
          var msg = 'You can reserve up to ' + reservedQty + ' coins at a time, so ' + reservedQty + ' were added to your cart.';
          if (window.rlcNotify) window.rlcNotify(msg); else say('info', msg);
        }
        restore();
      } catch (err) {
        console.error('[RLC] heritage reserve failed:', err);
        if (!added) release();
        window.location.href = '/cart';
      }
    });
  }

  /* ── Coin photo thumbnails ───────────────────────── */
  function initThumbs(scope) {
    scope.querySelectorAll('.hx-coin__media').forEach(function (m) {
      if (m.__hxBound) return; m.__hxBound = true;
      var main = m.querySelector('.hx-coin__stage img');
      var thumbs = [].slice.call(m.querySelectorAll('.hx-coin__thumb'));
      if (!main || !thumbs.length) return;
      thumbs.forEach(function (t) {
        t.addEventListener('click', function () {
          main.removeAttribute('srcset');
          main.src = t.getAttribute('data-hx-src');
          main.alt = t.getAttribute('data-hx-alt') || '';
          thumbs.forEach(function (x) { var on = x === t; x.classList.toggle('is-on', on); x.setAttribute('aria-pressed', on ? 'true' : 'false'); });
        });
      });
    });
  }

  /* ── Gallery lightbox ────────────────────────────── */
  var lb = null, lbItems = [], lbIndex = 0, lbReturn = null;
  function buildLightbox() {
    lb = document.createElement('div');
    lb.className = 'hx hx-lightbox';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', 'Photograph');
    lb.innerHTML = '<figure><img alt=""><figcaption></figcaption></figure>' +
      '<button type="button" class="hx-lightbox__close" aria-label="Close">&times;</button>' +
      '<button type="button" class="hx-lightbox__prev" aria-label="Previous photograph">&#8249;</button>' +
      '<button type="button" class="hx-lightbox__next" aria-label="Next photograph">&#8250;</button>';
    document.body.appendChild(lb);
    lb.querySelector('.hx-lightbox__close').addEventListener('click', closeLB);
    lb.querySelector('.hx-lightbox__prev').addEventListener('click', function () { showLB(lbIndex - 1); });
    lb.querySelector('.hx-lightbox__next').addEventListener('click', function () { showLB(lbIndex + 1); });
    lb.addEventListener('click', function (e) { if (e.target === lb) closeLB(); });
    document.addEventListener('keydown', function (e) {
      if (!lb.classList.contains('is-open')) return;
      if (e.key === 'Escape') closeLB();
      else if (e.key === 'ArrowRight') showLB(lbIndex + 1);
      else if (e.key === 'ArrowLeft') showLB(lbIndex - 1);
      else if (e.key === 'Tab') {
        var f = [].slice.call(lb.querySelectorAll('button'));
        var i = f.indexOf(document.activeElement);
        e.preventDefault();
        f[(i + (e.shiftKey ? f.length - 1 : 1)) % f.length].focus();
      }
    });
  }
  function showLB(i) {
    lbIndex = (i + lbItems.length) % lbItems.length;
    var b = lbItems[lbIndex];
    var img = lb.querySelector('img');
    var inner = b.querySelector('img');
    img.src = b.getAttribute('data-hx-full');
    img.alt = inner ? inner.alt : '';
    lb.querySelector('figcaption').textContent = b.getAttribute('data-hx-caption') || '';
    var multi = lbItems.length > 1;
    lb.querySelector('.hx-lightbox__prev').hidden = !multi;
    lb.querySelector('.hx-lightbox__next').hidden = !multi;
  }
  function openLB(items, i, from) {
    if (!lb) buildLightbox();
    lbItems = items; lbReturn = from;
    showLB(i);
    lb.classList.add('is-open');
    root.style.overflow = 'hidden';
    lb.querySelector('.hx-lightbox__close').focus();
  }
  function closeLB() {
    lb.classList.remove('is-open');
    root.style.overflow = '';
    if (lbReturn) lbReturn.focus();
  }
  function initGallery(scope) {
    scope.querySelectorAll('[data-hx-gallery]').forEach(function (g) {
      if (g.__hxBound) return; g.__hxBound = true;
      var btns = [].slice.call(g.querySelectorAll('[data-hx-full]'));
      btns.forEach(function (b, i) { b.addEventListener('click', function () { openLB(btns, i, b); }); });
    });
  }

  /* ── Mobile shop bar ─────────────────────────────── */
  function initSticky(scope) {
    scope.querySelectorAll('[data-hx-sticky]').forEach(function (bar) {
      if (bar.__hxBound || !('IntersectionObserver' in window)) return;
      bar.__hxBound = true;
      var section = bar.closest('.hx-coin');
      var buy = section && section.querySelector('[data-hx-buy]');
      var hero = document.querySelector('.hx-hero');
      var link = bar.querySelector('a');
      var heroVisible = !!hero, buyVisible = false;
      function update() {
        var on = !heroVisible && !buyVisible;
        bar.classList.toggle('is-on', on);
        bar.setAttribute('aria-hidden', on ? 'false' : 'true');
        if (link) link.setAttribute('tabindex', on ? '0' : '-1');
      }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.target === hero) heroVisible = e.isIntersecting;
          else buyVisible = e.isIntersecting;
        });
        update();
      }, { threshold: 0 });
      if (hero) io.observe(hero);
      if (buy) io.observe(buy);
      update();
    });
  }

  /* ── Smooth scroll to in-page heritage anchors ───── */
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[href^="#"]');
    if (!a) return;
    var id = a.getAttribute('href').slice(1);
    if (!id) return;
    var target = document.getElementById(id);
    if (!target || !target.classList.contains('hx')) return;
    e.preventDefault();
    var y = target.getBoundingClientRect().top + window.pageYOffset - headerOffset();
    window.scrollTo({ top: y, behavior: reduceMotion ? 'auto' : 'smooth' });
    if (history.replaceState) history.replaceState(null, '', '#' + id);
  });

  /* ── Boot ────────────────────────────────────────── */
  function initAll(scope) {
    scope = scope || document;
    initReveal(scope);
    scope.querySelectorAll('[data-hx-buy]').forEach(initBuy);
    initThumbs(scope);
    initGallery(scope);
    initSticky(scope);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(); });
  else initAll();
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
